import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { MessageCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const imgSrc = (u) => (!u ? "" : u.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);
const money = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

const STATUS_STYLE = {
    "In Stock": "bg-green-100 text-green-800",
    "Out of Stock": "bg-red-100 text-red-700",
    "Made to Order": "bg-amber-100 text-amber-800",
};

export const Catalog = () => {
    const [cats, setCats] = useState([]);
    const [products, setProducts] = useState([]);
    const [cat, setCat] = useState("all");
    const [sub, setSub] = useState("all");

    useEffect(() => {
        axios.get(`${API}/categories`).then((r) => setCats(r.data)).catch(() => {});
        axios.get(`${API}/products`).then((r) => setProducts(r.data)).catch(() => {});
    }, []);

    const activeCat = useMemo(() => cats.find((c) => c.id === cat), [cats, cat]);
    const filtered = products.filter(
        (p) => (cat === "all" || p.category_id === cat) && (sub === "all" || p.sub_id === sub)
    );

    if (products.length === 0) return null;

    const waFor = (p) =>
        `https://wa.me/917799157203?text=${encodeURIComponent(
            `Hello Zam Zam Furnitures, I want a quote for: ${p.title}${p.sub_name ? ` (${p.sub_name})` : ""}.`
        )}`;

    const chip = (active) =>
        `px-5 py-2.5 font-mono text-[11px] tracking-[0.15em] uppercase border transition-colors duration-300 ${
            active ? "bg-navy text-white border-navy" : "bg-white text-charcoal border-line hover:border-gold hover:text-gold"
        }`;

    return (
        <section id="shop" data-testid="catalog-section" className="bg-bone py-24 lg:py-32 border-t border-line">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                    className="mb-12"
                >
                    <p className="font-mono text-[11px] tracking-[0.35em] text-gold mb-6" data-testid="catalog-overline">
                        LIVE CATALOG · FACTORY PRICES
                    </p>
                    <h2 className="font-heading font-extrabold tracking-tighter text-navy text-4xl sm:text-5xl lg:text-6xl leading-[1.05]" data-testid="catalog-heading">
                        BROWSE. PICK. ENQUIRE.
                    </h2>
                </motion.div>

                <div className="flex flex-wrap gap-2 mb-4" data-testid="catalog-category-filters">
                    <button onClick={() => { setCat("all"); setSub("all"); }} className={chip(cat === "all")} data-testid="filter-all">
                        All Products
                    </button>
                    {cats.map((c) => (
                        <button key={c.id} onClick={() => { setCat(c.id); setSub("all"); }} className={chip(cat === c.id)} data-testid={`filter-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                            {c.name}
                        </button>
                    ))}
                </div>
                {activeCat && activeCat.subs.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4" data-testid="catalog-subcategory-filters">
                        <button onClick={() => setSub("all")} className={chip(sub === "all")} data-testid="subfilter-all">
                            All {activeCat.name}
                        </button>
                        {activeCat.subs.map((s) => (
                            <button key={s.id} onClick={() => setSub(s.id)} className={chip(sub === s.id)} data-testid={`subfilter-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                                {s.name}
                            </button>
                        ))}
                    </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8" data-testid="catalog-grid">
                    {filtered.map((p, i) => (
                        <motion.div
                            key={p.id}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-40px" }}
                            transition={{ duration: 0.5, delay: (i % 4) * 0.06 }}
                            className="border border-line bg-white flex flex-col hover:border-gold transition-colors duration-300"
                            data-testid={`catalog-product-${p.id}`}
                        >
                            <div className="relative h-[240px] bg-white flex items-center justify-center p-5 border-b border-line">
                                {p.image_url ? (
                                    <img src={imgSrc(p.image_url)} alt={p.title} loading="lazy" className="max-h-full w-auto object-contain" />
                                ) : (
                                    <span className="text-muted text-sm">Photo coming soon</span>
                                )}
                                <span className={`absolute top-3 left-3 text-[10px] font-semibold px-2 py-1 ${STATUS_STYLE[p.status] || STATUS_STYLE["Made to Order"]}`}>
                                    {p.status}
                                </span>
                            </div>
                            <div className="p-5 flex-1 flex flex-col">
                                <p className="font-mono text-[10px] tracking-[0.2em] text-gold uppercase">
                                    {p.category_name}{p.sub_name ? ` · ${p.sub_name}` : ""}
                                </p>
                                <h3 className="font-heading font-bold tracking-tight text-navy text-lg mt-1.5">{p.title}</h3>
                                {p.description && <p className="text-sm text-muted mt-2 leading-relaxed line-clamp-2">{p.description}</p>}
                                <div className="mt-4 flex-1 flex flex-col justify-end">
                                    {p.sale_price || p.mrp ? (
                                        <div>
                                            {p.sale_price && <span className="font-heading font-extrabold text-navy text-2xl tracking-tight">{money(p.sale_price)}</span>}
                                            {p.mrp && p.sale_price && p.mrp > p.sale_price && (
                                                <span className="ml-2 text-sm text-muted line-through">{money(p.mrp)}</span>
                                            )}
                                            {!p.sale_price && p.mrp && <span className="font-heading font-extrabold text-navy text-2xl tracking-tight">{money(p.mrp)}</span>}
                                            {p.gst !== null && p.gst !== undefined && p.gst !== "" && (
                                                <p className="text-[11px] text-muted mt-1">+ {p.gst}% GST{p.delivery ? ` · ${p.delivery}` : ""}</p>
                                            )}
                                        </div>
                                    ) : (
                                        <p className="font-heading font-bold text-navy text-lg">Price on request</p>
                                    )}
                                    <a href={waFor(p)} target="_blank" rel="noopener noreferrer"
                                        className="mt-4 inline-flex items-center justify-center gap-2 w-full bg-navy text-white py-3 text-sm font-semibold hover:bg-gold transition-colors"
                                        data-testid={`enquire-${p.id}`}>
                                        <MessageCircle size={15} /> Enquire on WhatsApp
                                    </a>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
                {filtered.length === 0 && (
                    <p className="text-muted text-sm mt-8" data-testid="catalog-empty-filter">No products in this selection yet — check another category.</p>
                )}
            </div>
        </section>
    );
};
